/**
 * TrueVault Polaris page-interrupt adapter: claims `window.UFT.PageInterrupts` while the
 * Polaris consent banner is up, so popups wait for it. The only file that knows TrueVault
 * exists; another vendor is another adapter.
 *
 * Driven by Polaris's `consentChanged` event, not Shopify's customerPrivacy API, which
 * reports what was consented to rather than whether a banner is up: US opt-out states
 * default consent to all-true with the banner still on screen.
 * https://developers.truevault.com/docs/resources/consent-changed-event
 *
 * The banner element's presence is the claim (Polaris publishes no visibility field);
 * `consentChanged` is the release (emitted on every documented close); the removal poll
 * backstops a close the vendor never documented. Mobile only: on a phone the banner and a
 * popup both take the bottom of the screen.
 */

window.UFT = window.UFT || {};
window.UFT.TrueVaultPageInterrupt =
	window.UFT.TrueVaultPageInterrupt ||
	(function () {
		const HOLD_NAME = 'truevault-consent';
		const CONSENT_CHANGED_EVENT = 'consentChanged';
		// Also Popup.MEDIA_QUERIES.MOBILE_BREAKPOINT in assets/popup.js and the media blocks
		// in sections/popups.liquid; change all three together.
		const MOBILE_BREAKPOINT = '(max-width: 767px)';
		// Container-scoped first as the more specific. Removed or hidden in place, _isBannerVisible
		// reads both as gone. Read off the store's bundle, so a vendor rebuild can move them.
		const BANNER_SELECTORS = ['#polaris-consent-widgets .polaris-banner', '.polaris-banner'];
		// Only a close Polaris never reports waits this long; every documented close emits
		// consentChanged. Well inside the registry's ninety-second default, so the hold cannot
		// lapse between ticks.
		const BANNER_REMOVAL_POLL_MS = 5 * 1000;
		// Each reconcile forces a style recalculation, so mutations are batched. A timer, not a
		// frame: browsers pause requestAnimationFrame in a hidden tab, where the banner most often
		// mounts after this file starts.
		const RECONCILE_COALESCE_MS = 50;
		// A pending choice with no banner after this long is a region that shows none; the watch
		// ends so a body-wide observer is not left running.
		const BANNER_MOUNT_WAIT_MS = 5 * 1000;

		class TrueVaultPageInterrupt {
			#removalPollTimer = null;
			#bannerObserver = null;
			#reconcileTimer = null;
			#mountDeadline = null;
			#bannerSeen = false;
			// Replayed when the viewport crosses the breakpoint: a rotation fires no consent event.
			#lastDetail = null;

			constructor() {
				this._handleConsentChanged = this._handleConsentChanged.bind(this);
				this._handleViewportChange = this._handleViewportChange.bind(this);
				this._reconcileBannerWatch = this._reconcileBannerWatch.bind(this);
				this._scheduleBannerReconcile = this._scheduleBannerReconcile.bind(this);
				this._endWatchIfBannerNeverMounted = this._endWatchIfBannerNeverMounted.bind(this);
			}

			start() {
				window.addEventListener(CONSENT_CHANGED_EVENT, this._handleConsentChanged);
				this._listenForViewportChanges();

				// Polaris fires its bootstrap event before this deferred file runs, so the inlined
				// buffer holds it.
				this._resyncFromBuffer();
			}

			_handleConsentChanged(event) {
				this._recordAndDecide(event.detail);
			}

			_resyncFromBuffer() {
				const buffered = window.UFT?.truevaultConsentBuffer;
				if (buffered) {
					this._recordAndDecide(buffered);
				}
			}

			_recordAndDecide(detail) {
				this.#lastDetail = detail;
				this._decideForDetail(detail);
			}

			// Recomputed per detail rather than tracked; claims and releases are idempotent.
			_decideForDetail(detail) {
				if (!this._holdRegistry()) {
					return;
				}

				if (!this._isChoicePending(detail) || !this._isMobileViewport()) {
					this._endHold();
					return;
				}

				this._watchBanner();
			}

			// A matchMedia stub without the listener API is common in tests; a page without matchMedia
			// is already desktop per _isMobileViewport.
			_listenForViewportChanges() {
				if (typeof window.matchMedia !== 'function') {
					return;
				}

				const viewport = window.matchMedia(MOBILE_BREAKPOINT);
				if (typeof viewport.addEventListener !== 'function') {
					return;
				}

				viewport.addEventListener('change', this._handleViewportChange);
			}

			// A phone loading in landscape is desktop-wide and starts no watch; rotating to
			// portrait fires no Polaris event, so the last one is decided again at the new width.
			_handleViewportChange() {
				if (this.#lastDetail === null) {
					return;
				}

				this._decideForDetail(this.#lastDetail);
			}

			// `implicit` means no explicit choice yet. The informational banner reports its own
			// dismissal, since dismissing it changes no consent value.
			_isChoicePending(detail) {
				return detail?.implicit === true && detail?.informationalConsentBannerDismissed !== true;
			}

			// Tearing the watch down is part of ending the hold: a live watcher would re-claim it.
			_endHold() {
				this._stopBannerWatch();
				this.#bannerSeen = false;
				this._holdRegistry()?.release(HOLD_NAME);
			}

			// Split because the phases end at different moments: a sighting ends mount detection and
			// starts the poll; only the hold ending takes both down. clearInterval/clearTimeout accept null.
			_stopBannerWatch() {
				this._stopMountDetection();
				clearInterval(this.#removalPollTimer);
				this.#removalPollTimer = null;
			}

			_stopMountDetection() {
				clearTimeout(this.#mountDeadline);
				this.#mountDeadline = null;
				clearTimeout(this.#reconcileTimer);
				this.#reconcileTimer = null;
				this.#bannerObserver?.disconnect();
				this.#bannerObserver = null;
			}

			_watchBanner() {
				if (!this._isWatching()) {
					this._startMountDetection();
				}

				this._reconcileBannerWatch();
			}

			// Both phases: the sighting nulls #bannerObserver, so a guard on that alone would arm a
			// fresh body-wide observer on every consentChanged while the choice is pending.
			_isWatching() {
				return this.#bannerObserver !== null || this.#removalPollTimer !== null;
			}

			// The batch is the signal, the records noise. Disconnected at the sighting: the banner's
			// removal is reported by consentChanged.
			_startMountDetection() {
				this.#mountDeadline = setTimeout(this._endWatchIfBannerNeverMounted, BANNER_MOUNT_WAIT_MS);
				this.#bannerObserver = new MutationObserver(this._scheduleBannerReconcile);
				// Attributes too: a banner rendered hidden and revealed by a class flip mounts before the
				// observer arms, so childList alone would miss it. The debounce absorbs lazysizes class swaps.
				this.#bannerObserver.observe(document.body, {
					childList: true,
					subtree: true,
					attributes: true,
					attributeFilter: ['class', 'style', 'hidden'],
				});
			}

			// Reached only when no banner ever mounted; the sighting clears the deadline.
			_endWatchIfBannerNeverMounted() {
				this.#mountDeadline = null;

				if (!this.#bannerSeen) {
					this._endHold();
				}
			}

			// The body, because the banner's container does not exist yet when the watch arms.
			// Coalescing makes the wide root affordable for the seconds it is up.
			_scheduleBannerReconcile() {
				if (this.#reconcileTimer !== null) {
					return;
				}

				this.#reconcileTimer = setTimeout(() => {
					this.#reconcileTimer = null;
					this._reconcileBannerWatch();
				}, RECONCILE_COALESCE_MS);
			}

			// Presence claims and renews the hold and hands the watch to the poll. Absence after a
			// sighting is the backstop, not the release: every documented close emits consentChanged
			// first. Absence before a sighting is left to the mount deadline.
			_reconcileBannerWatch() {
				if (!this._isMobileViewport()) {
					this._endHold();
					return;
				}

				if (this._findBannerElement() !== null) {
					this.#bannerSeen = true;
					this._startRemovalPoll();
					this._claimHold();
					return;
				}

				if (this.#bannerSeen) {
					this._endHold();
				}
			}

			// Mount detection comes down before the already-armed return: a repeat _watchBanner that
			// slipped past _isWatching would otherwise leave its observer on the body for the page view.
			_startRemovalPoll() {
				this._stopMountDetection();

				if (this.#removalPollTimer !== null) {
					return;
				}

				this.#removalPollTimer = setInterval(this._reconcileBannerWatch, BANNER_REMOVAL_POLL_MS);
			}

			// Width, not device class: the constraint is screen space, and a tablet has desktop room.
			// Classic max-width syntax because the range form is Safari 16.4, above this file's floor.
			_isMobileViewport() {
				return typeof window.matchMedia === 'function' && window.matchMedia(MOBILE_BREAKPOINT).matches;
			}

			_findBannerElement() {
				for (let index = 0; index < BANNER_SELECTORS.length; index += 1) {
					const candidate = document.querySelector(BANNER_SELECTORS[index]);
					if (candidate && this._isBannerVisible(candidate)) {
						return candidate;
					}
				}

				return null;
			}

			// Both properties: this is the only thing that ends the hold on a close Polaris does not
			// report, and a banner hidden by anything this misses reads as up for the page view. Erring
			// towards "gone" is safe: a card beside an invisible banner, not a card that never comes.
			// No geometry test: getClientRects() cannot tell hidden from unmeasured.
			_isBannerVisible(element) {
				if (!element.isConnected || element.hidden) {
					return false;
				}

				if (window.getComputedStyle(element).visibility === 'hidden') {
					return false;
				}

				return !this._isDisplayNoneOnOrAbove(element);
			}

			// visibility inherits, so one read answers for the ancestors; display does not, and Polaris
			// hides the #polaris-consent-widgets wrapper, an ancestor of both BANNER_SELECTORS.
			_isDisplayNoneOnOrAbove(element) {
				let node = element;

				while (node) {
					if (window.getComputedStyle(node).display === 'none') {
						return true;
					}

					node = node.parentElement;
				}

				return false;
			}

			_holdRegistry() {
				return window.UFT?.PageInterrupts;
			}

			_claimHold() {
				this._holdRegistry()?.claim(HOLD_NAME);
			}
		}

		const adapter = new TrueVaultPageInterrupt();
		adapter.start();
		return adapter;
	})();
